import { useEffect, useState } from "react";
import { Loader2, Plus, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { avatarFor } from "@/lib/avatar";
import { PageHeader } from "@/components/admin/ui";

export default function ReviewsPage() {
  const [reviews, setReviews] = useState(null);
  const [form, setForm] = useState({ name: "", text: "", rating: 5 });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/reviews").then((r) => setReviews(r.data)).catch(() => setReviews([]));
  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/reviews", form);
      toast.success("Avaliação adicionada ao site!");
      setForm({ name: "", text: "", rating: 5 });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    try {
      await api.delete(`/reviews/${r.id}`);
      toast.success("Avaliação removida");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const inputCls =
    "w-full rounded-xl border border-white/10 bg-[#0A0B0D] px-4 py-3 text-sm text-white placeholder-zinc-500 outline-none focus:border-[#FFB800]";

  if (!reviews) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 size={28} className="animate-spin text-[#FFB800]" />
      </div>
    );
  }

  return (
    <div data-testid="reviews-page">
      <PageHeader title="Depoimentos" sub="Avaliações exibidas na seção 'O que nossos clientes dizem' do site" testId="reviews-header" />

      <form onSubmit={submit} className="mb-6 rounded-2xl border border-white/5 bg-[#11141A] p-6" data-testid="review-form">
        <h3 className="mb-4 font-display text-lg font-bold uppercase text-white">
          Adicionar <span className="italic text-[#FFB800]">avaliação</span>
        </h3>
        <div className="grid gap-4 lg:grid-cols-[1fr_160px]">
          <input required data-testid="review-name-input" placeholder="Nome do cliente *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
          <select data-testid="review-rating-select" value={form.rating} onChange={(e) => setForm({ ...form, rating: Number(e.target.value) })} className={inputCls}>
            {[5, 4, 3, 2, 1].map((n) => (
              <option key={n} value={n} className="bg-[#0A0B0D]">{n} estrela{n > 1 ? "s" : ""}</option>
            ))}
          </select>
        </div>
        <textarea required data-testid="review-text-input" rows={2} placeholder="Texto da avaliação *" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} className={`${inputCls} mt-4 resize-none`} />
        <button type="submit" disabled={saving} data-testid="review-submit-button" className="mt-4 flex items-center gap-2 rounded-full bg-[#FFB800] px-6 py-3 font-display text-sm font-bold uppercase tracking-wider text-black transition-transform hover:scale-105 disabled:opacity-60">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} strokeWidth={3} />}
          {saving ? "Salvando..." : "Publicar no site"}
        </button>
      </form>

      <div className="grid gap-4 lg:grid-cols-2" data-testid="reviews-list">
        {reviews.map((r, i) => (
          <div key={r.id} className="rounded-2xl border border-white/5 bg-[#11141A] p-5" data-testid={`review-item-${i}`}>
            <div className="flex items-center gap-3">
              <img src={r.avatar || avatarFor(r.name)} alt={r.name} className="h-10 w-10 rounded-full border border-white/10 object-cover" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-white">{r.name}</p>
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} size={12} className={j < r.rating ? "fill-[#FFB800] text-[#FFB800]" : "fill-zinc-700 text-zinc-700"} />
                  ))}
                </div>
              </div>
              <span className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase ${r.source === "google" ? "border-[#4285F4]/50 text-[#4285F4]" : "border-[#FFB800]/50 text-[#FFB800]"}`} data-testid={`review-source-${i}`}>
                {r.source === "google" ? "Google" : "Adicionada"}
              </span>
              <button onClick={() => remove(r)} data-testid={`review-delete-${i}`} className="text-zinc-600 transition-colors hover:text-red-400" aria-label="Remover avaliação">
                <Trash2 size={16} />
              </button>
            </div>
            <p className="mt-3 text-sm italic leading-relaxed text-zinc-400">“{r.text}”</p>
          </div>
        ))}
      </div>
    </div>
  );
}
